import React from 'react';
import { SvgIconReact, SvgIconType } from 'common/components/svg-icon';
import { Translatable } from 'common/i18n';
import { useTranslation } from 'common/i18n/hooks';
import { react2panel } from 'common/util/react2panel';
import { withStyles } from 'common/util/react';
import { MarkdownReact } from 'common/components/markdown';
import { DEFAULT_SEARCH_QUERY_STRING } from 'common/report/util/search';
import { SearchSortOption } from 'common/report/util/search/types';
import { promptFeedbackModal } from '../feedback-modal';
import { FeedbackForFeature } from '../feedback-modal/types';
import { EmptyDashboardCreateHero } from '../warning-hero/presets-react';
import type { Browse } from './index';
import styles from './empty-state.module.styl';
import css from './empty-state.module.styl?inline';
type EmptyStateProps = {
  title?: Translatable;
  subtitle?: Translatable;
  icon?: string;
  children?: React.ReactNode;
};

// eslint-disable-next-line react/destructuring-assignment
const EmptyState = (_ref: EmptyStateProps) => {
  let {
    title = _ref2 => {
      let {
        t
      } = _ref2;
      return t("discover.no-results.title");
    },
    subtitle,
    icon = "search",
    children
  }: EmptyStateProps = _ref;
  const {
    print
  } = useTranslation();
  return <div className={styles.browseEmptyState} data-sentry-component="EmptyState" data-sentry-source-file="empty-state-react.tsx">
      <div className={styles.browseEmptyIcon}>
        <SvgIconReact icon={icon as SvgIconType} data-sentry-element="SvgIconReact" data-sentry-source-file="empty-state-react.tsx" />
      </div>
      <div className={styles.browseEmptyTitle}>{print(title)}</div>
      {subtitle ? <div className={styles.browseEmptySubtitle}>{print(subtitle)}</div> : null}
      <div className={styles.browseEmptyDetails}>{children}</div>
    </div>;
};
export const EmptyStateReact = withStyles(EmptyState, css);
const ErrorEmptyState = () => {
  const {
    t
  } = useTranslation();
  return <>
      <EmptyStateReact title={t("discover.error.title")} subtitle={t("discover.error.subtitle")} icon='warning' data-sentry-element="EmptyStateReact" data-sentry-source-file="empty-state-react.tsx">
        <MarkdownReact text={t("discover.error.body", {
        supportLink: "https://mixpanel.com/get-support",
        statusLink: "https://www.mixpanelstatus.com/",
        interpolation: {
          escapeValue: false
        }
      })} data-sentry-element="MarkdownReact" data-sentry-source-file="empty-state-react.tsx" />
      </EmptyStateReact>
    </>;
};
export const ErrorEmptyStateEl = react2panel("browse-error-empty-state", ErrorEmptyState);

// eslint-disable-next-line react/destructuring-assignment
export const NoResultsEmptyState = (_ref3: {
  cmp: Browse;
}) => {
  let {
    cmp
  }: {
    cmp: Browse;
  } = _ref3;
  const hasFilteredResults = cmp.params.searchText !== DEFAULT_SEARCH_QUERY_STRING || cmp.state.queryParams.sort === SearchSortOption.RecentlyViewed || cmp.getNumActiveFilters() > 0;
  if (!hasFilteredResults) {
    if (cmp.isFullPageMode()) {
      return <EmptyDashboardCreateHero onCreateDashboardClick={cmp.params.onCreateDashboardClicked} onOpenFoundationalTemplateModalClicked={cmp.params.onOpenFoundationalTemplateModalClicked} headerText={cmp.t("discover.no-results.full-page.title")} bodyText={cmp.t("discover.no-results.full-page.body")} />;
    }
    if (cmp.isAddMode()) {
      return <EmptyStateReact title={cmp.t("discover.no-results.add-mode.title")} subtitle={cmp.t("discover.no-results.add-mode.subtitle")} icon={"report"} />;
    }
    return <EmptyStateReact title={cmp.t("discover.no-results.no-entities")} />;
  }
  if (cmp.isFullPageMode()) {
    return <EmptyStateReact subtitle={cmp.t("discover.no-results.full-page.subtitle-filtered")}>
        <a className='.mp-link' onClick={() => {
        promptFeedbackModal({
          feature: FeedbackForFeature.Browse
        });
      }}>
          {cmp.t("discover.no-results.full-page.body-filtered")}
        </a>
      </EmptyStateReact>;
  }
  return <EmptyStateReact subtitle={cmp.t("discover.no-results.add-mode.subtitle-filtered")} data-sentry-element="EmptyStateReact" data-sentry-component="NoResultsEmptyState" data-sentry-source-file="empty-state-react.tsx" />;
};
export const NoResultsEmptyStateEl = react2panel("browse-error-empty-state", NoResultsEmptyState);